import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useContext } from 'react';
import AuthContext from '../../context/AuthContext';
import AdminServices from '../../services/AdminServices';
import TopNavbar from './TopNavbar';
// Old headers deleted - use topnav layout instead
import AdminSidebar from '../admin/sidebar';
import SchoolSidebar from '../school/sidebar';
import SchoolTeacherSidebar from '../schoolteacher/sidebar';
import TeacherSidebar from '../teacher/sidebar';
import { getMenuConfigForRole } from './menuConfigs';
import './DashboardLayout.css';

/**
 * DashboardLayout - Unified layout component that renders TopNavbar with mega menu
 * 
 * @param {Object} props
 * @param {string|number} props.role - User role ('admin', 'state', 1, 2, 3)
 * @param {string} props.userType - For School role, 'Individual' or 'School'
 * @param {string} props.layoutType - 'topnav' (defaults to 'topnav')
 * @param {React.ReactNode} props.children - Page content
 */
const DashboardLayout = ({
  role,
  userType,
  layoutType,
  children,
}) => {
  const { userrole } = useContext(AuthContext);

  // Determine layout type if not provided
  // Default to topnav with mega menu for all roles for consistent navigation
  const finalLayoutType = layoutType || 'topnav';

  // Get menu configuration for the role
  const finalRole = role || userrole;
  const baseMenu = getMenuConfigForRole(finalRole, userType);

  // School admins (role '1') on a MANUAL (non-auto-renew) subscription get a "Renew" nav
  // nudge captioned with their expiration date, blinking within 30 days of it. Auto-renewing
  // schools (CIM/ARB on file) never see it. Non-fatal — nav just omits it on any error.
  const [renewItem, setRenewItem] = useState(null);
  useEffect(() => {
    if (String(finalRole) !== '1') { setRenewItem(null); return; }
    let cancelled = false;
    (async () => {
      const sub = await AdminServices.getMySubscription();
      if (cancelled) return;
      if (!sub || !sub.hasSubscription || sub.isAutoRenew) { setRenewItem(null); return; }
      const exp = sub.expirationDate ? new Date(sub.expirationDate) : null;
      setRenewItem({
        label: 'Renew',
        to: '/renew',
        activePaths: ['/renew'],
        subtitle: exp
          ? `Expires ${exp.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
          : 'Renewal due',
        blink: typeof sub.daysUntilExpiration === 'number' && sub.daysUntilExpiration <= 30,
      });
    })();
    return () => { cancelled = true; };
  }, [finalRole]);

  // Inject Renew right after Home when applicable.
  const menuConfig = (renewItem && Array.isArray(baseMenu) && baseMenu.length)
    ? [baseMenu[0], renewItem, ...baseMenu.slice(1)]
    : baseMenu;

  // Render topnav layout with mega navbar
  if (finalLayoutType === 'topnav') {
    return (
      <div className="main-wrapper topnav-layout">
        <TopNavbar
          role={finalRole}
          menuItems={menuConfig}
        />
        <div className="page-content topnav-content">
          <div className="container-fluid">
            {children}
          </div>
        </div>
      </div>
    );
  }

  // Sidebar layout - OLD HEADERS DELETED
  // Old header components (AdminHeader, SchoolHeader, TeacherHeader, SchoolTeacherHeader) have been deleted.
  // All pages must use layoutType="topnav" to use the new mega navbar.
  throw new Error('Old header components have been deleted. Please use layoutType="topnav" to use the new mega navbar instead of the old sidebar layout.');
};

DashboardLayout.propTypes = {
  role: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  userType: PropTypes.string,
  layoutType: PropTypes.oneOf(['sidebar', 'topnav']),
  children: PropTypes.node.isRequired,
};

export default DashboardLayout;

